import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Briefcase, FileText, AlertCircle, Bookmark, PenTool, Calendar, ChevronDown, Lock, Tag } from 'lucide-react';
import { mandatiList, MandatoType, CategoriaMandato } from '../utils/mandatiData';
import { elementiFattoList } from '../utils/elementiFattoData';
import { ambitiMandato } from '../utils/data';
import mandatoPDF from "./MandatoPDF";
import { localIsoDate } from '../utils/utils';

interface BackendFieldDef {
    key: string;
    label: string;
    description: string;
    type: string;
    required: boolean;
}

interface Props {
    dynamicFields: BackendFieldDef[];
    formValues: any;
    setFormValues: (data: any) => void;
    selectedMandatoCode: string;
    setSelectedMandatoCode: (code: string) => void;
    selectedModelId: string;
    selectedAmbito?: string;
    lockCoreFields?: boolean;
    // Blocco del select "tipologia mandato". Di default segue `lockCoreFields`;
    // in modifica resta modificabile anche se cliente e modello sono bloccati.
    lockMandatoType?: boolean;
}

export const Step2_DatiPratica: React.FC<Props> = ({
                                                       dynamicFields, formValues, setFormValues,
                                                       selectedMandatoCode, setSelectedMandatoCode,
                                                       selectedModelId, selectedAmbito, lockCoreFields,
                                                       lockMandatoType,
                                                   }) => {
    const mandatoLocked = lockMandatoType !== undefined ? lockMandatoType : !!lockCoreFields;

    // Stati per gestire l'apertura dei menu personalizzati
    const [openMandatoDropdown, setOpenMandatoDropdown] = useState(false);
    const [openElementiDropdown, setOpenElementiDropdown] = useState(false);

    // Riferimenti per gestire i click "fuori" per chiudere le tendine
    const dropdownMandatoRef = useRef<HTMLDivElement>(null);
    const dropdownElementiRef = useRef<HTMLDivElement>(null);

    const allowedCategories = useMemo((): CategoriaMandato[] => {
        switch (selectedModelId) {
            case 'prev-standard': return ['AMBITO PRIVATO']; // vecchio id modello
            case 'mand-investigativo': {
                const ambito = ambitiMandato.find(a => a.id === selectedAmbito);
                if (ambito) return [ambito.categoria as CategoriaMandato];
                return ['AMBITO PRIVATO', 'AMBITO AZIENDALE'];
            }
            case 'prev-bonifiche': return ['BONIFICHE'];
            case 'info-privacy': return ['STUDI LEGALI'];
            case 'prev-consulenza': return ['ATTIVITA DI CONSULENZA'];
            default: return ['AMBITO PRIVATO', 'AMBITO AZIENDALE', 'BONIFICHE', 'STUDI LEGALI', 'ATTIVITA DI CONSULENZA'];
        }
    }, [selectedModelId, selectedAmbito]);

    const groupedMandati = useMemo(() => {
        return mandatiList
            .filter(m => allowedCategories.includes(m.category))
            .reduce((acc, curr) => {
                if (!acc[curr.category]) acc[curr.category] = [];
                acc[curr.category].push(curr);
                return acc;
            }, {} as Record<string, MandatoType[]>);
    }, [allowedCategories]);

    const groupedElementi = useMemo(() => {
        return elementiFattoList
            .filter(el => {
                if (el.category === 'GENERICO') return true;
                if (!selectedMandatoCode) return false;
                if ((el as any).mandatiCollegati && Array.isArray((el as any).mandatiCollegati)) {
                    return (el as any).mandatiCollegati.includes(selectedMandatoCode);
                }
                return allowedCategories.includes(el.category as CategoriaMandato);
            })
            .reduce((acc, curr) => {
                if (!acc[curr.category]) acc[curr.category] = [];
                acc[curr.category].push(curr);
                return acc;
            }, {} as Record<string, typeof elementiFattoList>);
    }, [allowedCategories, selectedMandatoCode]);

    useEffect(() => {
        if (!formValues.data_incarico) {
            const today = localIsoDate(); // Format: YYYY-MM-DD
            setFormValues((prev: any) => ({ ...prev, data_incarico: today }));
        }
    }, [formValues.data_incarico, setFormValues]);

    // Gestione click "fuori" dalle tendine per chiuderle
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownMandatoRef.current && !dropdownMandatoRef.current.contains(event.target as Node)) {
                setOpenMandatoDropdown(false);
            }
            if (dropdownElementiRef.current && !dropdownElementiRef.current.contains(event.target as Node)) {
                setOpenElementiDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Scorre fino al blocco; "scroll-mt-24" evita che finisca sotto la barra in alto.
    const scrollUpToSeeDropdown = (ref: React.RefObject<HTMLDivElement | null>) => {
        if (ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleChange = (key: string, value: string) => {
        setFormValues((prev: any) => ({ ...prev, [key]: value }));
    };

    const handleMandatoSelection = (newCode: string, fieldKey: string) => {
        setSelectedMandatoCode(newCode);
        console.log(newCode)
        const mandato = mandatiList.find(x => x.code === newCode);

        // Pulisce il titolo da eventuali quadre iniziali (es. "[cod1] Nome" -> "Nome")
        const cleanTitle = mandato ? mandato.title.replace(/^\[.*?\]\s*/, '').trim() : '';

        setFormValues((prev: any) => ({
            ...prev,
            [fieldKey]: newCode,
            'oggetto_mandato_text': mandato ? mandato.description : '',
            'interesse_diritto_tutelato': cleanTitle
        }));
        setOpenMandatoDropdown(false); // Chiude la tendina
    };

    const handleElementiSelection = (text: string, fieldKey: string) => {
        handleChange(fieldKey, text);
        setOpenElementiDropdown(false); // Chiude la tendina
    };

    return (
        <div className="animate-in fade-in slide-in-from-right-8 max-w-4xl mx-auto pb-24">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="bg-gray-50 px-8 py-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <Briefcase size={24} className="text-blue-600"/> Dettagli Incarico e Mandato
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">Compila i riferimenti e definisci l'oggetto del mandato.</p>
                </div>

                <div className="p-8 space-y-8">

                    {/* Data incarico */}
                    <div className="bg-blue-50/40 p-4 rounded-lg border border-blue-100">
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-bold text-gray-800 mb-1 flex items-center gap-1">
                                    <Calendar size={14} className="text-blue-500"/> Data Conferimento Incarico
                                </label>
                                <div className="text-xs text-gray-500">Data in cui viene stipulato il contratto</div>
                            </div>
                            <div className="w-full md:w-48">
                                <input type="date" className="w-full p-2.5 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                       value={formValues.data_incarico || ''}
                                       onChange={e => handleChange('data_incarico', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {/* Riferimento: etichetta libera per riconoscere il mandato
                        nella tabella /pratiche, modificabile anche da lì. */}
                    <div className="bg-amber-50/40 p-4 rounded-lg border border-amber-100">
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-bold text-gray-800 mb-1 flex items-center gap-1">
                                    <Tag size={14} className="text-amber-500"/> Riferimento <span className="text-gray-500 font-normal text-xs ml-1">(Opzionale)</span>
                                </label>
                                <div className="text-xs text-gray-500">Città, punto vendita o soprannome per riconoscere il mandato nell'elenco (max 120 caratteri).</div>
                            </div>
                            <div className="w-full md:w-80">
                                <input
                                    type="text"
                                    maxLength={120}
                                    className="w-full p-2.5 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none"
                                    placeholder="Es. Milano / Esselunga V.le Roma / Sig. Rossi"
                                    value={formValues.riferimento || ''}
                                    onChange={e => handleChange('riferimento', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {dynamicFields.map((field) => {

                        // Numero pratica
                        if (field.key === 'rif_numero_pratica') {
                            return (
                                <div key={field.key} className="bg-blue-50/50 p-4 rounded-lg border border-blue-100 flex flex-col md:flex-row md:items-center gap-4">
                                    <div className="flex-1">
                                        <label className="block text-sm font-bold text-gray-800 mb-1">
                                            {field.label} <span className="text-gray-500 font-normal text-xs ml-1">(Opzionale)</span>
                                        </label>
                                        <div className="text-xs text-gray-500">{field.description}</div>
                                    </div>
                                    <div className="w-full md:w-64">
                                        <div className="relative">
                                            <Bookmark className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400" size={18}/>
                                            <input type="text"
                                                   className="w-full pl-10 pr-4 py-2.5 border border-blue-200 rounded-lg font-mono font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                                                   placeholder="Generato in automatico"
                                                   value={formValues[field.key] || ''}
                                                   onChange={e => handleChange(field.key, e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        // Selezione mandato (dropdown custom)
                        if (field.type === 'select-mandato') {
                            return (
                                <div key={field.key} className="space-y-2 relative scroll-mt-24" ref={dropdownMandatoRef}>
                                    <label className="block text-sm font-bold text-gray-800 flex items-center justify-between">
                                        <span>{field.label} {field.required && !mandatoLocked && <span className="text-red-500">*</span>}</span>
                                        {mandatoLocked && <span className="text-xs font-normal text-gray-400 flex items-center gap-1"><Lock size={12}/> Bloccato</span>}
                                    </label>

                                    <div
                                        className={`w-full p-3 border rounded-lg flex items-center justify-between transition-colors ${mandatoLocked ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-80' : 'bg-white border-gray-300 cursor-pointer hover:border-blue-400 focus:ring-2 focus:ring-blue-500 outline-none'}`}
                                        onClick={() => {
                                            if (!mandatoLocked) {
                                                const willOpen = !openMandatoDropdown;
                                                setOpenMandatoDropdown(willOpen);
                                                if (willOpen) setTimeout(() => scrollUpToSeeDropdown(dropdownMandatoRef), 100);
                                            }
                                        }}
                                        tabIndex={mandatoLocked ? -1 : 0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                if (!mandatoLocked) {
                                                    const willOpen = !openMandatoDropdown;
                                                    setOpenMandatoDropdown(willOpen);
                                                    if (willOpen) setTimeout(() => scrollUpToSeeDropdown(dropdownMandatoRef), 100);
                                                }
                                            }
                                        }}
                                    >
                                        <span className={`text-sm ${selectedMandatoCode ? (mandatoLocked ? 'text-gray-600 font-bold' : 'text-gray-900 font-medium') : 'text-gray-500'}`}>
                                            {selectedMandatoCode
                                                ? `[${selectedMandatoCode}] ${mandatiList.find(m => m.code === selectedMandatoCode)?.title}`
                                                : '-- Seleziona la tipologia di incarico --'}
                                        </span>
                                        {!mandatoLocked && (
                                            <ChevronDown size={18} className={`text-gray-400 transition-transform ${openMandatoDropdown ? 'rotate-180' : ''}`} />
                                        )}
                                    </div>

                                    {/* Menu a discesa Custom per Mandati */}
                                    {openMandatoDropdown && !mandatoLocked && (
                                        <div className="absolute top-[calc(100%+8px)] left-0 w-full z-50 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-[350px] overflow-y-auto animate-in fade-in zoom-in-95 origin-top">
                                            {Object.keys(groupedMandati).map(cat => (
                                                <div key={cat}>
                                                    <div className="bg-gray-50 px-4 py-2 text-xs font-bold text-gray-500 uppercase sticky top-0 border-b border-t border-gray-100 first:border-t-0 shadow-sm z-10 backdrop-blur-sm bg-gray-50/90">
                                                        {cat}
                                                    </div>
                                                    {groupedMandati[cat].map((m) => (
                                                        <div key={m.code}
                                                             className="px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-blue-50 transition-colors"
                                                             onClick={() => handleMandatoSelection(m.code, field.key)}>
                                                            <div className="font-bold text-sm text-gray-800">[{m.code}] {m.title}</div>
                                                            <div className="text-xs text-gray-500 mt-1 line-clamp-2">{m.description}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {field.description && <p className="text-xs text-gray-500">{field.description}</p>}
                                </div>
                            );
                        }

                        // Oggetto mandato e interesse/diritto tutelato
                        if (field.key === 'oggetto_mandato_text') return (
                            <div key="interesse_diritto_container" className="space-y-6">
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-1 flex justify-between">
                                        <span>Descrizione Oggetto Mandato</span>
                                        <span className="text-xs text-gray-400 font-normal flex items-center gap-1"><PenTool size={10}/> Modificabile</span>
                                    </label>
                                    <textarea
                                        className="w-full p-4 border border-gray-300 rounded-lg min-h-[120px] focus:ring-2 focus:ring-blue-500 outline-none text-sm leading-relaxed shadow-sm"
                                        value={formValues[field.key] || ''}
                                        onChange={e => handleChange(field.key, e.target.value)}
                                        placeholder="Seleziona un mandato sopra per pre-compilare questo campo..." />
                                </div>

                                {/* Interesse/diritto */}
                                <div className="bg-amber-50/30 p-4 rounded-lg border border-amber-100">
                                    <label className="block text-sm font-bold text-amber-900 mb-1 uppercase tracking-tight">
                                        Interesse o diritto che si intende tutelare o esercitare:
                                    </label>
                                    <p className="text-xs text-amber-700 mb-3 italic">
                                        [compilare solo se l'interesse/diritto non è già indicato o desumibile nel precedente campo "oggetto"]
                                    </p>
                                    <textarea
                                        className="w-full p-3 border border-amber-200 rounded-lg min-h-[80px] bg-white focus:ring-2 focus:ring-amber-500 outline-none text-sm"
                                        value={formValues.interesse_diritto_tutelato || ''}
                                        onChange={e => handleChange('interesse_diritto_tutelato', e.target.value)}
                                        placeholder="Inserire qui l'interesse o diritto specifico..." />
                                </div>
                            </div>
                        );

                        // Elementi di fatto (non per bonifiche e consulenze)
                        if (field.type === 'select-elementi-fatto' && (selectedModelId === 'prev-bonifiche' || selectedModelId === 'prev-consulenza')) return null;

                        // Elementi di fatto (dropdown custom)
                        if (field.type === 'select-elementi-fatto') {
                            return (
                                <div key={field.key} className="bg-gray-50 p-6 rounded-xl border border-gray-200 space-y-4 relative scroll-mt-24" ref={dropdownElementiRef}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <FileText size={20} className="text-gray-600"/>
                                        <h3 className="font-bold text-gray-800">{field.label}</h3>
                                    </div>

                                    <div className="relative">
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                                            Sfoglia i modelli predefiniti per farti un'idea
                                        </label>

                                        <div
                                            className={`w-full p-2.5 border rounded-lg bg-white text-sm flex items-center justify-between cursor-pointer hover:border-blue-400 transition-colors focus:ring-2 focus:ring-blue-500 outline-none ${openElementiDropdown ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-300'}`}
                                            onClick={() => {
                                                const willOpen = !openElementiDropdown;
                                                setOpenElementiDropdown(willOpen);
                                                if (willOpen) setTimeout(() => scrollUpToSeeDropdown(dropdownElementiRef), 100);
                                            }}
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    const willOpen = !openElementiDropdown;
                                                    setOpenElementiDropdown(willOpen);
                                                    if (willOpen) setTimeout(() => scrollUpToSeeDropdown(dropdownElementiRef), 100);
                                                }
                                            }}
                                        >
                                            <span className="text-gray-500">-- Clicca qui per sfogliare e selezionare un modello --</span>
                                            <ChevronDown size={18} className={`text-gray-400 transition-transform ${openElementiDropdown ? 'rotate-180' : ''}`} />
                                        </div>

                                        {/* Menu a discesa Custom per Elementi di Fatto */}
                                        {openElementiDropdown && (
                                            <div className="absolute top-[calc(100%+8px)] left-0 w-full z-50 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-[350px] overflow-y-auto animate-in fade-in zoom-in-95 origin-top">
                                                {Object.keys(groupedElementi).map(cat => (
                                                    <div key={cat}>
                                                        <div className="bg-gray-50 px-4 py-2 text-xs font-bold text-gray-500 uppercase sticky top-0 border-b border-t border-gray-100 first:border-t-0 shadow-sm z-10 backdrop-blur-sm bg-gray-50/90">
                                                            {cat}
                                                        </div>
                                                        {groupedElementi[cat].map((el) => (
                                                            <div key={el.id}
                                                                 className="px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-blue-50 transition-colors group"
                                                                 onClick={() => handleElementiSelection(el.text, field.key)}>
                                                                <div className="font-bold text-sm text-blue-700 group-hover:text-blue-800">{el.label}</div>
                                                                <div className="text-xs text-gray-600 mt-1.5 italic line-clamp-3">"{el.text}"</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Contenuto (Modificabile)</label>
                                        <textarea className="w-full p-3 border border-gray-300 rounded-lg min-h-[150px] bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                                  value={formValues[field.key] || ''}
                                                  onChange={e => handleChange(field.key, e.target.value)}
                                                  placeholder="Seleziona un modello o scrivi liberamente qui..." />
                                    </div>
                                    <div className="flex gap-2 text-xs text-gray-500 bg-white p-2 rounded border border-gray-100">
                                        <AlertCircle size={14} className="text-blue-500 shrink-0"/>
                                        <p>Puoi selezionare un modello dal menu a tendina per pre-compilarlo oppure scrivere liberamente nel campo di testo.</p>
                                    </div>
                                </div>
                            );
                        }

                        // Campo generico
                        return (
                            <div key={field.key}>
                                <label className="block text-sm font-bold text-gray-800 mb-1">
                                    {field.label} {field.required && <span className="text-red-500">*</span>}
                                </label>
                                {field.type === 'textarea' ? (
                                    <textarea className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" rows={4}
                                              value={formValues[field.key] || ''} onChange={e => handleChange(field.key, e.target.value)} />
                                ) : (
                                    <input type={field.type === 'date' ? 'date' : 'text'}
                                           className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                           value={formValues[field.key] || ''} onChange={e => handleChange(field.key, e.target.value)} />
                                )}
                                {field.description && <p className="text-xs text-gray-500 mt-1">{field.description}</p>}
                            </div>
                        );
                    })}

                    {/* Consulenza: tipologia attività */}
                    {selectedModelId === 'prev-consulenza' && (
                        <div className="bg-indigo-50/40 p-6 rounded-xl border border-indigo-200 space-y-4">
                            <h3 className="font-bold text-indigo-900 flex items-center gap-2 text-sm uppercase tracking-wide">
                                <FileText size={16} className="text-indigo-600"/> Tipologia di Attività Richiesta
                            </h3>
                            <p className="text-xs text-indigo-700">Seleziona una o più attività oggetto dell'incarico:</p>

                            {([
                                { key: 'tipo_consulenza_investigativa', label: 'consulenza tecnico-investigativa' },
                                { key: 'tipo_criminologica',            label: 'consulenza criminologica, ricostruttiva, preventiva o di analisi del rischio' },
                                { key: 'tipo_tecnica_parte',            label: 'consulenza tecnica di parte e/o supporto a legale, consulente, azienda o privato' },
                                { key: 'tipo_indagine_aziendale',       label: 'consulenza preliminare finalizzata alla valutazione di fattibilità di un\'indagine in ambito privato, familiare, personale o patrimoniale,\n' +
                                        'nei limiti della licenza e della normativa applicabile;' },
                                { key: 'tipo_accertamenti_dipendenti',  label: 'consulenza preliminare finalizzata alla valutazione di fattibilità di un\'indagine in ambito aziendale, commerciale, assicurativo,\n' +
                                        'difensivo o di tutela del patrimonio aziendale;' },
                                { key: 'tipo_analisi_documentale',      label: 'analisi documentale, analisi atti, ricostruzione cronologica dei fatti, verifica di incongruenze o anomalie' },
                                { key: 'tipo_analisi_dati',             label: 'analisi, estrazione, acquisizione, recupero o organizzazione di dati da computer, smartphone, tablet, supporti digitali, archivi, e-mail o altri sistemi legittimamente disponibili' },
                                { key: 'tipo_informatica_forense',      label: 'analisi informatica, informatica forense, copia tecnica, duplicazione, estrazione, preservazione o verifica di dati digitali' },
                                { key: 'tipo_analisi_media',            label: 'analisi di fotografie, filmati, file audio, conversazioni, documenti, tabulati, log, messaggi, tracciati, report, flussi contabili o altri elementi forniti dal Mandante' },
                                { key: 'tipo_sopralluogo',              label: 'sopralluogo tecnico, ricognizione, ispezione documentale o verifica di luoghi, beni, dispositivi, procedure o materiali legittimamente messi a disposizione' },
                                { key: 'tipo_osint',                    label: 'attività OSINT, ricerca da fonti aperte, verifica reputazionale o raccolta di informazioni pubblicamente accessibili, nei limiti di legge' },
                                { key: 'tipo_redazione_relazione',      label: 'redazione di relazione tecnica, parere, verbale, report riepilogativo o elaborato consulenziale' },
                            ] as { key: string; label: string }[]).map(({ key, label }) => (
                                <label key={key} className="flex items-start gap-3 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        className="mt-0.5 h-4 w-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                                        checked={!!formValues[key]}
                                        onChange={e => setFormValues((prev: any) => ({ ...prev, [key]: e.target.checked }))}
                                    />
                                    <span className="text-sm text-gray-700 group-hover:text-indigo-800 leading-snug">{label};</span>
                                </label>
                            ))}

                            {/* Altra attività */}
                            <label className="flex items-start gap-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    className="mt-0.5 h-4 w-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                                    checked={!!formValues.tipo_altra_attivita}
                                    onChange={e => setFormValues((prev: any) => ({ ...prev, tipo_altra_attivita: e.target.checked }))}
                                />
                                <span className="text-sm text-gray-700 group-hover:text-indigo-800">altra attività:</span>
                            </label>
                            {formValues.tipo_altra_attivita && (
                                <input
                                    type="text"
                                    className="w-full p-2.5 border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm ml-7"
                                    placeholder="Descrivi l'attività..."
                                    value={formValues.tipo_altra_attivita_testo || ''}
                                    onChange={e => setFormValues((prev: any) => ({ ...prev, tipo_altra_attivita_testo: e.target.value }))}
                                />
                            )}
                        </div>
                    )}

                    {/* Consulenza: finalità */}
                    {selectedModelId === 'prev-consulenza' && (
                        <div className="bg-amber-50/40 p-6 rounded-xl border border-amber-200 space-y-4">
                            <h3 className="font-bold text-amber-900 flex items-center gap-2 text-sm uppercase tracking-wide">
                                <FileText size={16} className="text-amber-600"/> Finalità dell'Incarico
                            </h3>
                            <p className="text-xs text-amber-700">Il Mandante dichiara che la finalità dell'incarico è la seguente:</p>

                            {([
                                { key: 'finalita_diritto_giudiziario', label: 'tutela di un diritto in sede giudiziaria o stragiudiziale' },
                                { key: 'finalita_patrimonio',          label: 'tutela del patrimonio personale, familiare o aziendale' },
                                { key: 'finalita_verifica_fatti',      label: 'verifica, ricostruzione o documentazione di fatti rilevanti' },
                                { key: 'finalita_supporto_legale',     label: 'supporto a legale, consulente tecnico, azienda, privato o professionista' },
                                { key: 'finalita_prevenzione_rischio', label: 'prevenzione, gestione o valutazione di un rischio' },
                            ] as { key: string; label: string }[]).map(({ key, label }) => (
                                <label key={key} className="flex items-start gap-3 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        className="mt-0.5 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500 shrink-0"
                                        checked={!!formValues[key]}
                                        onChange={e => setFormValues((prev: any) => ({ ...prev, [key]: e.target.checked }))}
                                    />
                                    <span className="text-sm text-gray-700 group-hover:text-amber-800 leading-snug">{label};</span>
                                </label>
                            ))}

                            {/* Altra finalità */}
                            <label className="flex items-start gap-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    className="mt-0.5 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500 shrink-0"
                                    checked={!!formValues.finalita_altra}
                                    onChange={e => setFormValues((prev: any) => ({ ...prev, finalita_altra: e.target.checked }))}
                                />
                                <span className="text-sm text-gray-700 group-hover:text-amber-800">altra finalità lecita:</span>
                            </label>
                            {formValues.finalita_altra && (
                                <input
                                    type="text"
                                    className="w-full p-2.5 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none text-sm ml-7"
                                    placeholder="Descrivi la finalità..."
                                    value={formValues.finalita_altra_testo || ''}
                                    onChange={e => setFormValues((prev: any) => ({ ...prev, finalita_altra_testo: e.target.value }))}
                                />
                            )}
                        </div>
                    )}

                    {/* Consulenza: compenso */}
                    {selectedModelId === 'prev-consulenza' && (
                        <div className="bg-green-50/40 p-6 rounded-xl border border-green-200 space-y-4">
                            <h3 className="font-bold text-green-900 flex items-center gap-2 text-sm uppercase tracking-wide">
                                <Briefcase size={16} className="text-green-600"/> Compenso Concordato
                            </h3>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Importo (€) + IVA</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">€</span>
                                    <input
                                        type="text"
                                        className="w-full pl-8 p-2.5 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-500 outline-none font-mono"
                                        placeholder="Es. 500,00"
                                        value={formValues.compenso_concordato || ''}
                                        onChange={e => setFormValues((prev: any) => ({ ...prev, compenso_concordato: e.target.value }))}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bonifica: campi specifici */}
                    {selectedModelId === 'prev-bonifiche' && (
                        <div className="bg-teal-50/40 p-6 rounded-xl border border-teal-200 space-y-5">
                            <h3 className="font-bold text-teal-900 flex items-center gap-2 text-sm uppercase tracking-wide">
                                <Calendar size={16} className="text-teal-600"/> Programmazione e Compenso del Servizio
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Data Inizio Servizio</label>
                                    <input type="date" className="w-full p-2.5 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                           value={formValues.data_inizio_servizio || ''}
                                           onChange={e => handleChange('data_inizio_servizio', e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-700 mb-1">Orario Inizio Servizio</label>
                                    <input type="time" className="w-full p-2.5 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                           value={formValues.orario_inizio_servizio || ''}
                                           onChange={e => handleChange('orario_inizio_servizio', e.target.value)} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Termine del Servizio</label>
                                <input type="text" className="w-full p-2.5 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                       placeholder="Es. Entro 24h dall'inizio, oppure data specifica..."
                                       value={formValues.termine_servizio || ''}
                                       onChange={e => handleChange('termine_servizio', e.target.value)} />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Compenso Concordato (€)</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">€</span>
                                    <input type="text" className="w-full pl-8 p-2.5 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none font-mono"
                                           placeholder="Es. 350,00"
                                           value={formValues.compenso_concordato || ''}
                                           onChange={e => handleChange('compenso_concordato', e.target.value)} />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Importo totale concordato per il servizio di bonifica</p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Luogo e Data</label>
                                <input type="text" className="w-full p-2.5 border border-teal-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                       placeholder="Es. Chieti, 14/05/2026"
                                       value={formValues.luogo_data_approvazione || ''}
                                       onChange={e => handleChange('luogo_data_approvazione', e.target.value)} />

                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};